import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth-context";
import {
  listVaultEntries, isVaultAdmin as isVaultAdminFn, canAddVaultEntry, VAULT_CATEGORIES, type VaultEntry,
} from "@/lib/vault";
import { VaultEntryCard } from "@/components/vault/VaultEntryCard";
import { AddVaultEntryModal } from "@/components/vault/AddVaultEntryModal";
import { AccessLogsModal } from "@/components/vault/AccessLogsModal";

export const Route = createFileRoute("/_app/vault_/entries")({ component: EntriesPage });

function EntriesPage() {
  const { profile, role } = useAuth();
  const isVaultAdmin = isVaultAdminFn(role);
  const canAdd = canAddVaultEntry(role, profile);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<VaultEntry | null>(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<string>("all");
  const [userTab, setUserTab] = useState<"shared" | "owned" | "renewal">("shared");

  const { data: entries = [] } = useQuery({
    queryKey: ["vault-entries"],
    queryFn: listVaultEntries,
    enabled: !!profile,
  });

  const filtered = useMemo(() => {
    let list = entries;
    if (!isVaultAdmin && profile) {
      if (userTab === "owned") list = list.filter((e) => e.owner_id === profile.id);
      else if (userTab === "renewal") list = list.filter((e) => !!e.renewal_date);
      else list = list.filter((e) => e.owner_id !== profile.id);
    }
    if (filter !== "all") list = list.filter((e) => e.category === filter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((e) =>
        [e.name, e.tool_url, e.tool_id].some((v) => v?.toLowerCase().includes(s)),
      );
    }
    return list;
  }, [entries, isVaultAdmin, profile, userTab, filter, search]);

  const openModal = (e: VaultEntry) => { setEditing(e); setAddOpen(true); };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">
            Good morning, {profile?.full_name?.split(" ")[0] ?? "there"} 👋
          </h1>
          <p className="text-sm text-muted-foreground">
            {isVaultAdmin
              ? "All tools, credentials, and links in one place"
              : "Entries shared with you and entries owned by you"}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search vault entries..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        {isVaultAdmin ? (
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {VAULT_CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex gap-1">
            {(["shared", "owned", "renewal"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setUserTab(t)}
                className={`px-3 py-1.5 rounded-md text-sm border ${
                  userTab === t ? "bg-foreground text-background border-foreground" : "bg-background border-border"
                }`}
              >
                {t === "shared" ? "Shared With Me" : t === "owned" ? "Owned By Me" : "Renewal Due"}
              </button>
            ))}
          </div>
        )}
        <div className="ml-auto flex gap-2">
          {canAdd && (
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => { setEditing(null); setAddOpen(true); }}>
              + Add Entry
            </Button>
          )}
          {isVaultAdmin && (
            <>
              <Button variant="outline" onClick={() => setLogsOpen(true)}>Access Logs</Button>
              <Button variant="outline" disabled>Export Disabled</Button>
            </>
          )}
        </div>
      </div>

      <div className={`grid gap-4 ${isVaultAdmin ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 md:grid-cols-2"}`}>
        {filtered.map((e) => (
          <VaultEntryCard
            key={e.id}
            entry={e}
            currentUserId={profile?.id ?? ""}
            role={role}
            onOpen={openModal}
            onEdit={openModal}
            onShare={openModal}
            onTransfer={openModal}
          />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 border border-dashed border-border rounded-xl space-y-3">
            <div className="text-base font-semibold">No vault entries found</div>
            <div className="text-sm text-muted-foreground">Click + Add Entry to save your first tool.</div>
            {canAdd && (
              <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => { setEditing(null); setAddOpen(true); }}>
                + Add Entry
              </Button>
            )}
          </div>
        )}
      </div>

      {!isVaultAdmin && (
        <div className="rounded-xl border border-border bg-card p-5 text-sm space-y-2">
          <div className="text-xs uppercase text-muted-foreground">Ownership Rule</div>
          <div>Only the current owner, Admin, and Moderator can edit an entry.</div>
          <div>If ownership is transferred, the new owner gets edit access.</div>
          <div>At one time, each entry has only one non-admin owner.</div>
        </div>
      )}

      <AddVaultEntryModal open={addOpen} onOpenChange={setAddOpen} editing={editing} />
      <AccessLogsModal open={logsOpen} onOpenChange={setLogsOpen} />
    </div>
  );
}
